/**
 * Páginas — the dashboard routes each tenant exposes to BFF content.
 *
 * Cards grouped into a collapsible section per BU. Every write goes through the mutations, which
 * invalidate the list, so promoting a page to main shows the demoted one correctly without the
 * client guessing.
 *
 * @module Pages/BUs/Pages
 */

import { css } from '@emotion/css';
import { UTButton, UTLoading, UTSelect } from '@group-link-one/gl-ui/utilities';
import { useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useCreatePage, useDeletePage, usePages, useUpdatePage } from '../../Hooks/usePagesQueries';
import type { Page } from '../../Types/Pages.types';
import { groupPagesByTenant } from '../../Utils/groupPagesByTenant';
import { DeletePageModal } from '../DeletePageModal/DeletePageModal';
import { PageCard } from '../PageCard/PageCard';
import type { PageFormValues } from '../PageFormModal/PageFormModal';
import { PageFormModal } from '../PageFormModal/PageFormModal';
import { usePrivileges } from '@/Hooks/usePrivileges/usePrivilleges';
import { useTenants } from '@/Hooks/useTenants';
import { getTenantColor, getTenantIcon } from '@/Shared/brand';
import { CollapsibleSection } from '@/Shared/components/CollapsibleSection/CollapsibleSection';
import { EmptyState } from '@/Shared/components/EmptyState/EmptyState';
import { MainContent } from '@/Templates/Main/MainContent/MainContent';
import { TruncatedListNotice } from '@/Shared/components/TruncatedListNotice/TruncatedListNotice';
import { isListTruncated } from '@/Shared/constants/listLimits';

const screenStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-base);
`;

const toolbarStyle = css`
  display: flex;
  align-items: flex-start;
  gap: var(--space-base);
  flex-wrap: wrap;
`;

const descriptionStyle = css`
  margin: 0;
  max-width: 620px;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

const spacerStyle = css`
  flex: 1;
`;

const filterStyle = css`
  min-width: 200px;
`;

const groupsStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--space-ml);
`;

const cardsGridStyle = css`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(260px, 100%), 1fr));
  gap: var(--space-base);
`;

const ALL_TENANTS = 'all';

const PagesList = (): JSX.Element => {
  const { pages: privileges } = usePrivileges();
  const { tenants, tenantOptions } = useTenants();

  const [tenantFilter, setTenantFilter] = useState<string>(ALL_TENANTS);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<Page | undefined>();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<Page | undefined>();

  const { data, isLoading } = usePages();
  const createPage = useCreatePage();
  const updatePage = useUpdatePage();
  const deletePage = useDeletePage();

  const allPages = useMemo(() => data?.data ?? [], [data]);

  const groups = useMemo(() => {
    const scoped =
      tenantFilter === ALL_TENANTS
        ? allPages
        : allPages.filter(page => page.tenantSlug === tenantFilter);

    return groupPagesByTenant(scoped);
  }, [allPages, tenantFilter]);

  const filterOptions = useMemo(
    () => [
      { id: ALL_TENANTS, text: 'Todos os tenants', value: ALL_TENANTS },
      ...tenantOptions.map(option => ({
        id: String(option.id),
        text: option.text,
        value: String(option.value),
      })),
    ],
    [tenantOptions],
  );

  const handleSubmit = (values: PageFormValues) => {
    const onDone = () => {
      setIsFormOpen(false);
      setEditing(undefined);
    };

    if (editing) {
      updatePage.mutate(
        {
          id: editing.id,
          payload: { name: values.name, isMain: values.isMain, isActive: values.isActive },
        },
        { onSuccess: onDone },
      );
      return;
    }

    createPage.mutate(
      {
        tenantSlug: values.tenantSlug,
        slug: values.slug,
        name: values.name,
        isMain: values.isMain,
        isActive: values.isActive,
      },
      { onSuccess: onDone },
    );
  };

  if (isLoading) {
    return (
      <MainContent id='pages-main' styling={{ noBackgroundColor: true, noBorder: true }}>
        <UTLoading type='dots' />
      </MainContent>
    );
  }

  return (
    <MainContent id='pages-main' styling={{ noBackgroundColor: true, noBorder: true }}>
      <div className={screenStyle}>
        <div className={toolbarStyle}>
          <p className={descriptionStyle}>
            Rotas do dashboard de cada tenant. Uma página por tenant é a principal e recebe os
            anúncios criados sem página específica.
          </p>
          <div className={spacerStyle} />
          <div className={filterStyle}>
            <UTSelect
              options={filterOptions}
              canSelectMultiple={false}
              hasCheckbox={false}
              defaultSelectedOptions={[filterOptions[0]]}
              inputOptions={{ textFieldProps: { placeholder: 'Todos os tenants' } }}
              onSelectedOptionsChange={options => {
                setTenantFilter(options.length > 0 ? String(options[0].value) : ALL_TENANTS);
              }}
            />
          </div>

          {isListTruncated(data?.pagination.total, allPages.length) ? (
            <TruncatedListNotice
              id='pages-truncated'
              shown={allPages.length}
              total={data?.pagination.total ?? allPages.length}
              noun='páginas'
            />
          ) : null}
          {privileges.BUSINESS_UNITS.PAGES.CREATE ? (
            <UTButton
              fitContent
              id='pages-create'
              text='Criar página'
              icon='Arrows.Plus'
              onClick={() => {
                setEditing(undefined);
                setIsFormOpen(true);
              }}
            />
          ) : null}
        </div>

        {groups.length === 0 ? (
          <EmptyState
            id='pages-empty'
            icon='Menu.SquaresFour'
            title='Nenhuma página cadastrada'
            description="Aponte uma tela do dashboard para receber What's New e tours do BFF."
            action={
              privileges.BUSINESS_UNITS.PAGES.CREATE ? (
                <UTButton
                  fitContent
                  text='Criar página'
                  icon='Arrows.Plus'
                  onClick={() => {
                    setEditing(undefined);
                    setIsFormOpen(true);
                  }}
                />
              ) : undefined
            }
          />
        ) : (
          <div className={groupsStyle}>
            {groups.map(group => {
              const tenant = tenants.find(item => item.slug === group.tenantSlug);

              return (
                <CollapsibleSection
                  key={group.tenantSlug}
                  id={`pages-section-${group.tenantSlug}`}
                  title={group.tenantSlug}
                  monoTitle
                  icon={getTenantIcon({ slug: group.tenantSlug, iconName: tenant?.iconName })}
                  iconColor={getTenantColor(group.tenantSlug)}
                  count={group.countLabel}
                  summary={group.activeLabel}
                  open={!collapsed[group.tenantSlug]}
                  onToggle={() => {
                    setCollapsed(current => ({
                      ...current,
                      [group.tenantSlug]: !current[group.tenantSlug],
                    }));
                  }}
                >
                  <div className={cardsGridStyle}>
                    {group.pages.map(page => (
                      <PageCard
                        key={page.id}
                        page={page}
                        canEdit={privileges.BUSINESS_UNITS.PAGES.EDIT}
                        canDelete={privileges.BUSINESS_UNITS.PAGES.DELETE}
                        onToggleActive={(target, isActive) => {
                          updatePage.mutate({ id: target.id, payload: { isActive } });
                        }}
                        onEdit={target => {
                          setEditing(target);
                          setIsFormOpen(true);
                        }}
                        onDelete={setDeleting}
                      />
                    ))}
                  </div>
                </CollapsibleSection>
              );
            })}
          </div>
        )}
      </div>

      <PageFormModal
        open={isFormOpen}
        page={editing}
        tenantOptions={filterOptions.slice(1)}
        allPages={allPages}
        isSaving={createPage.isPending || updatePage.isPending}
        onClose={() => {
          setIsFormOpen(false);
          setEditing(undefined);
        }}
        onSubmit={handleSubmit}
      />

      {/* UTModal keeps its content in the a11y tree when closed, so mount it only when needed. */}
      {deleting ? (
        <DeletePageModal
          open
          page={deleting}
          isDeleting={deletePage.isPending}
          onClose={() => {
            setDeleting(undefined);
          }}
          onConfirm={target => {
            deletePage.mutate(target.id, {
              onSuccess: () => {
                setDeleting(undefined);
              },
            });
          }}
        />
      ) : null}
    </MainContent>
  );
};

export { PagesList };
